import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './NetSuiteBillDialog.css';
import NetSuiteBillForm from './NetSuiteBillForm';
import NetSuiteReview from './NetSuiteReview';
import JsonPayloadPane from './JsonPayloadPane';
import { createNetSuiteBill, OcrApiError } from '../api/ocrClient';
import {
  NetSuiteBillPayload,
  NetSuiteBillResponse,
  NetSuiteReviewRequired,
  NetSuiteUnresolvedNames,
} from '../types';
import {
  BillErrors,
  ExtractedTotals,
  LineMapping,
  NameIssue,
  applyLineMapping,
  formatMoney,
  initialLineMapping,
  lineMappingChecks,
  linesTotal,
  mappingConfirmations,
  nameIssues,
  newExternalId,
  openIssueErrors,
  prepareBillRequest,
  purchaseOrderLines,
  readReviewRequired,
  readUnresolvedNames,
  rememberBill,
  responseMessage,
  summarizeBillResponse,
  upstreamRequestId,
  validateBill,
  withConfirmedChecks,
} from '../utils/netsuite';

export interface BillSubmission {
  payload: NetSuiteBillPayload;
  /** null when NetSuite never confirmed and the user found the bill there themselves. */
  response: NetSuiteBillResponse | null;
}

interface NetSuiteBillDialogProps {
  filename: string;
  draft: NetSuiteBillPayload;
  onDraftChange: (next: NetSuiteBillPayload) => void;
  /** Re-map the draft from the OCR result, discarding edits. */
  onReset: () => void;
  extracted: ExtractedTotals;
  /** Set once the bill exists; the dialog then shows the outcome instead of the editor. */
  submission: BillSubmission | null;
  onSubmitted: (submission: BillSubmission) => void;
  onClose: () => void;
}

interface SubmitError {
  title: string;
  message: string;
  code?: string;
  requestId?: string;
  response?: unknown;
  /** The outcome is unknown (timeout) — the bill may exist. */
  uncertain?: boolean;
}

/** NetSuite wants differences confirmed, or bill lines matched to order lines, before it creates the bill. */
interface ReviewState {
  data: NetSuiteReviewRequired;
  confirmed: string[];
  mapping: LineMapping;
  raw?: unknown;
}

/** NetSuite couldn't match some names; the form shows its candidates and the fields at fault. */
interface NamesState {
  data: NetSuiteUnresolvedNames;
  issues: NameIssue[];
  raw?: unknown;
}

const rawResponse = (err: unknown) =>
  err instanceof OcrApiError ? (err.details as { response?: unknown } | undefined)?.response : undefined;

function toSubmitError(err: unknown): SubmitError {
  if (!(err instanceof OcrApiError)) {
    return {
      title: "Couldn't reach the server",
      message: 'Check your connection and try again. Your edits are kept.',
    };
  }
  // The request went out but no verdict came back — our wait ran out, or the
  // function was killed and the gateway answered with a non-JSON error page.
  if (err.code === 'NETSUITE_TIMEOUT' || (err.code === 'INVALID_RESPONSE' && err.status >= 500)) {
    const waitedMs = (err.details as { waitedMs?: number } | undefined)?.waitedMs;
    return {
      title: 'Sent to NetSuite — no confirmation yet',
      message:
        `NetSuite didn't answer ${waitedMs ? `within ${Math.round(waitedMs / 1000)} seconds` : 'in time'}, ` +
        "but it may still have created the bill. Check NetSuite first, so it isn't created twice.",
      code: err.code,
      uncertain: true,
    };
  }
  if (err.status === 401) {
    return {
      title: 'Your session has expired',
      message: 'Sign in again, then retry. Your edits are kept.',
      code: err.code,
    };
  }
  return {
    title: err.status === 422 ? 'NetSuite rejected the bill' : "The bill couldn't be created",
    message: err.message,
    code: err.code,
    requestId: upstreamRequestId(err.details) || undefined,
    response: rawResponse(err),
  };
}

const stringify = (v: unknown) => (typeof v === 'string' ? v : JSON.stringify(v, null, 2));

const SubmitErrorBanner: React.FC<{ error: SubmitError }> = ({ error }) => (
  <div className="nsb__alert" role="alert">
    <span className="nsb__alert-icon" aria-hidden="true">
      ⚠
    </span>
    <div className="nsb__alert-body">
      <p className="nsb__alert-title">{error.title}</p>
      <p className="nsb__alert-msg">{error.message}</p>
      {(error.code || error.requestId) && (
        <span className="nsb__alert-code">
          {[error.code, error.requestId && `request ${error.requestId}`].filter(Boolean).join(' · ')}
        </span>
      )}
      {error.response !== undefined && error.response !== null && (
        <details className="nsb__raw">
          <summary>Full response from NetSuite</summary>
          <pre>{stringify(error.response)}</pre>
        </details>
      )}
    </div>
  </div>
);

const issueLabel = (path: string) => {
  const line = /^items\.(\d+)\./.exec(path);
  return line ? `Line ${Number(line[1]) + 1}` : 'Vendor';
};

/** Lists what NetSuite couldn't match; each entry ticks itself off once its field changes. */
const UnresolvedBanner: React.FC<{
  issues: NameIssue[];
  open: BillErrors;
  requestId?: string;
  raw?: unknown;
  onShow: (path: string) => void;
}> = ({ issues, open, requestId, raw, onShow }) => {
  const openCount = issues.filter((issue) => open[issue.path]).length;
  return (
    <div className={`nsb__alert ${openCount ? 'nsb__alert--warn' : 'nsb__alert--ok'}`} role="status">
      <span className="nsb__alert-icon" aria-hidden="true">
        {openCount ? '🔎' : '✓'}
      </span>
      <div className="nsb__alert-body">
        <p className="nsb__alert-title">
          {openCount
            ? `NetSuite couldn't match ${openCount === 1 ? 'one name' : `${openCount} names`}`
            : 'Everything is sorted — create the bill again'}
        </p>
        <ul className="nsb__issues">
          {issues.map((issue) => {
            const isOpen = Boolean(open[issue.path]);
            return (
              <li key={issue.path} className={isOpen ? undefined : 'is-resolved'}>
                <span aria-hidden="true">{isOpen ? '•' : '✓'}</span>
                <span className="nsb__issue-text">
                  <strong>{issueLabel(issue.path)}:</strong> {issue.message}
                </span>
                {isOpen && (
                  <button type="button" className="link-btn nsb__issue-show" onClick={() => onShow(issue.path)}>
                    Show
                  </button>
                )}
              </li>
            );
          })}
        </ul>
        {requestId && <span className="nsb__alert-code">request {requestId}</span>}
        {raw !== undefined && raw !== null && (
          <details className="nsb__raw">
            <summary>Full response from NetSuite</summary>
            <pre>{stringify(raw)}</pre>
          </details>
        )}
      </div>
    </div>
  );
};

/** Shown instead of an error when the outcome is unknown: the user must check NetSuite before anything else. */
const TimeoutNotice: React.FC<{
  error: SubmitError;
  payload: NetSuiteBillPayload;
  onFound: () => void;
  onRetry: () => void;
}> = ({ error, payload, onFound, onRetry }) => (
  <div className="nsb__alert nsb__alert--warn" role="alert">
    <span className="nsb__alert-icon" aria-hidden="true">
      ⏳
    </span>
    <div className="nsb__alert-body">
      <p className="nsb__alert-title">{error.title}</p>
      <p className="nsb__alert-msg">{error.message}</p>
      <dl className="nsb__lookup">
        <div>
          <dt>Search NetSuite for external ID</dt>
          <dd>{payload.externalId}</dd>
        </div>
        <div>
          <dt>or vendor invoice number</dt>
          <dd>{payload.vendorInvoiceNumber}</dd>
        </div>
      </dl>
      <div className="nsb__alert-actions">
        <button type="button" className="nsb__btn nsb__btn--ok" onClick={onFound}>
          ✓ It's in NetSuite
        </button>
        <button type="button" className="nsb__btn nsb__btn--secondary" onClick={onRetry}>
          It's not there — try again
        </button>
      </div>
    </div>
  </div>
);

const SuccessView: React.FC<{ submission: BillSubmission; currency: string }> = ({
  submission,
  currency,
}) => {
  const { payload, response } = submission;
  const facts = response ? summarizeBillResponse(response.data) : [];
  const vendor = payload.vendorName || payload.vendorEntityId;
  const lead =
    (response && responseMessage(response.data)) ||
    `${vendor} · invoice ${payload.vendorInvoiceNumber}`;
  const confirmedChecks = Array.isArray(payload.confirmedChecks) ? payload.confirmedChecks : [];
  const matchedLines = payload.items.filter((item) => item.purchaseOrderLine !== undefined).length;

  return (
    <div className="nsb__success" role="status">
      <div className="nsb__success-badge" aria-hidden="true">
        <svg viewBox="0 0 48 48">
          <path className="nsb__check" d="M13 25l7 7 15-16" />
        </svg>
      </div>
      <h3 className="nsb__success-title">
        {response ? 'Bill created in NetSuite' : 'Bill found in NetSuite'}
      </h3>
      <p className="nsb__success-lead">{lead}</p>
      {!response && (
        <p className="nsb__success-note">
          NetSuite didn't confirm in time and you verified the bill there, so no NetSuite IDs are shown.
        </p>
      )}

      <dl className="nsb__facts">
        {facts.map((fact) => (
          <div className="nsb__fact nsb__fact--highlight" key={fact.label}>
            <dt>{fact.label}</dt>
            <dd>{fact.value}</dd>
          </div>
        ))}
        <div className="nsb__fact">
          <dt>Vendor</dt>
          <dd>{vendor}</dd>
        </div>
        <div className="nsb__fact">
          <dt>Invoice number</dt>
          <dd>{payload.vendorInvoiceNumber}</dd>
        </div>
        <div className="nsb__fact">
          <dt>Lines · total</dt>
          <dd>
            {payload.items.length} · {formatMoney(linesTotal(payload.items), currency)}
          </dd>
        </div>
        {matchedLines > 0 && (
          <div className="nsb__fact">
            <dt>Matched to order lines</dt>
            <dd>
              {matchedLines} of {payload.items.length}
            </dd>
          </div>
        )}
        {!facts.some((fact) => fact.label === 'External ID') && (
          <div className="nsb__fact">
            <dt>External ID</dt>
            <dd>{payload.externalId}</dd>
          </div>
        )}
        {confirmedChecks.length > 0 && (
          <div className="nsb__fact">
            <dt>Confirmed differences</dt>
            <dd>{confirmedChecks.join(', ')}</dd>
          </div>
        )}
        {response?.upstreamMs !== undefined && (
          <div className="nsb__fact">
            <dt>NetSuite answered in</dt>
            <dd>{(response.upstreamMs / 1000).toFixed(1)} s</dd>
          </div>
        )}
      </dl>

      {response && (
        <details className="nsb__raw">
          <summary>Full response (HTTP {response.upstreamStatus})</summary>
          <pre>{stringify(response.data)}</pre>
        </details>
      )}
    </div>
  );
};

const NetSuiteBillDialog: React.FC<NetSuiteBillDialogProps> = ({
  filename,
  draft,
  onDraftChange,
  onReset,
  extracted,
  submission,
  onSubmitted,
  onClose,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const formPaneRef = useRef<HTMLDivElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<SubmitError | null>(null);
  const [review, setReview] = useState<ReviewState | null>(null);
  const [names, setNames] = useState<NamesState | null>(null);
  /** The body of the latest attempt — what a timeout notice refers to. */
  const [lastRequest, setLastRequest] = useState<NetSuiteBillPayload | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [jsonInvalid, setJsonInvalid] = useState(false);
  const [tab, setTab] = useState<'form' | 'json'>('form');
  const [confirmReset, setConfirmReset] = useState(false);

  // Native modal <dialog>: focus trap, Escape, inert background and top-layer
  // stacking for free. Page scroll is locked while it is open.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    if (!confirmReset) return undefined;
    const timer = window.setTimeout(() => setConfirmReset(false), 3000);
    return () => window.clearTimeout(timer);
  }, [confirmReset]);

  const localErrors = useMemo(() => validateBill(draft), [draft]);
  const errorCount = Object.keys(localErrors).length;
  const issueErrors = useMemo<BillErrors>(
    () => (names ? openIssueErrors(names.issues, draft) : {}),
    [names, draft]
  );
  const openIssuePaths = Object.keys(issueErrors);
  const formErrors = useMemo(
    () => ({ ...(showErrors ? localErrors : {}), ...issueErrors }),
    [showErrors, localErrors, issueErrors]
  );
  const total = linesTotal(draft.items);

  // --- review: matches to make and differences to confirm ---
  const mappingChecks = useMemo(() => (review ? lineMappingChecks(review.data) : []), [review]);
  const poLines = useMemo(() => (review ? purchaseOrderLines(review.data) : []), [review]);
  const mappingIds = useMemo(() => new Set(mappingChecks.map((check) => check.checkId)), [mappingChecks]);
  const unmatched = review
    ? mappingChecks.filter((check) => review.mapping[check.itemIndex] === undefined).length
    : 0;
  const pendingChecks = review
    ? review.data.checks.filter((c) => c.confirmable && !review.confirmed.includes(c.id)).length
    : 0;
  // Non-confirmable checks that matching cannot fix: those need the bill corrected.
  const blockingChecks = review
    ? review.data.checks.filter((c) => !c.confirmable && !mappingIds.has(c.id)).length
    : 0;
  const reviewReady = !!review && pendingChecks === 0 && blockingChecks === 0 && unmatched === 0;

  /** The draft with the chosen order lines applied — what the confirmation sends. */
  const matchedDraft = useMemo(
    () => (review ? applyLineMapping(draft, review.mapping, poLines) : draft),
    [review, draft, poLines]
  );
  // While reviewing, the JSON pane previews exactly that request.
  const preview = useMemo(
    () => (review ? withConfirmedChecks(matchedDraft, review.confirmed) : draft),
    [review, matchedDraft, draft]
  );

  // Never close mid-request: the user must see whether the bill was created.
  const requestClose = useCallback(() => {
    if (!submitting) onClose();
  }, [submitting, onClose]);

  const focusFirstInvalid = () => {
    setTab('form');
    window.requestAnimationFrame(() => {
      const target = dialogRef.current?.querySelector<HTMLElement>(
        '[aria-invalid="true"], .nsb__lines-empty.is-invalid'
      );
      target?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      target?.focus({ preventScroll: true });
    });
  };

  /** Scroll to and focus the field an unresolved-name issue is about. */
  const showField = (path: string) => {
    setTab('form');
    window.requestAnimationFrame(() => {
      const line = /^items\.(\d+)\.(\w+)$/.exec(path);
      const target = line
        ? document.getElementById(`nsb-line-${line[1]}-${line[2]}`)
        : document.getElementById('nsb-vendor-picker') || document.getElementById(`nsb-${path}`);
      target?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      (target?.querySelector<HTMLElement>('input') || target)?.focus({ preventScroll: true });
    });
  };

  const submit = async (confirmedChecks: string[] = [], source?: NetSuiteBillPayload) => {
    if (submitting) return;
    const base = source || draft;
    setShowErrors(true);
    if (jsonInvalid) {
      setTab('json');
      return;
    }
    if (Object.keys(validateBill(base)).length > 0) {
      focusFirstInvalid();
      return;
    }

    const request = prepareBillRequest(base, confirmedChecks);
    setSubmitting(true);
    setError(null);
    setNames(null);
    setLastRequest(request);
    try {
      const response = await createNetSuiteBill(request);
      rememberBill(request);
      onSubmitted({ payload: request, response });
    } catch (err) {
      // That externalId is spent whatever happened: show the one the next attempt
      // will use, and keep any matching already applied to the lines.
      onDraftChange({ ...base, externalId: newExternalId(base.vendorInvoiceNumber) });

      const raw = rawResponse(err);
      const reviewData = err instanceof OcrApiError ? readReviewRequired(err.details) : null;
      const namesData = err instanceof OcrApiError ? readUnresolvedNames(err.details) : null;
      if (reviewData) {
        const previously = request.confirmedChecks || [];
        setReview({
          data: reviewData,
          raw,
          confirmed: reviewData.checks
            .filter((check) => check.confirmed || previously.includes(check.id))
            .map((check) => check.id),
          mapping: initialLineMapping(lineMappingChecks(reviewData), purchaseOrderLines(reviewData)),
        });
      } else {
        setReview(null);
        if (namesData) {
          setNames({ data: namesData, issues: nameIssues(namesData, request), raw });
        } else {
          setError(toSubmitError(err));
        }
      }
      setTab('form');
      formPaneRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleCheck = (id: string) =>
    setReview((current) =>
      current && {
        ...current,
        confirmed: current.confirmed.includes(id)
          ? current.confirmed.filter((checkId) => checkId !== id)
          : [...current.confirmed, id],
      }
    );

  const mapLine = (itemIndex: number, lineNumber: number | null) =>
    setReview((current) => {
      if (!current) return current;
      const mapping = { ...current.mapping };
      if (lineNumber === null) delete mapping[itemIndex];
      else mapping[itemIndex] = lineNumber;
      return { ...current, mapping };
    });

  const mapAll = (lineNumber: number) =>
    setReview((current) => {
      if (!current) return current;
      const mapping = { ...current.mapping };
      lineMappingChecks(current.data).forEach((check) => {
        mapping[check.itemIndex] = lineNumber;
      });
      return { ...current, mapping };
    });

  /** Send the matched, confirmed request. */
  const confirmAndCreate = () => {
    if (!review) return;
    if (mappingChecks.length > 0) onDraftChange(matchedDraft);
    // Items that were not on the order need their check confirmed as well as matched.
    const confirmedChecks = Array.from(
      new Set([...review.confirmed, ...mappingConfirmations(mappingChecks)])
    );
    submit(confirmedChecks, matchedDraft);
  };

  // After a timeout: the user looked in NetSuite and the bill is there.
  const markFound = () => {
    const sent = lastRequest || draft;
    rememberBill(sent);
    onSubmitted({ payload: sent, response: null });
  };

  const reset = () => {
    if (!confirmReset) {
      setConfirmReset(true);
      return;
    }
    setConfirmReset(false);
    setShowErrors(false);
    setError(null);
    setNames(null);
    onReset();
  };

  let status: React.ReactNode;
  if (review) {
    if (unmatched > 0) {
      status = (
        <span className="nsb__status nsb__status--pending">
          Match {unmatched} more invoice {unmatched === 1 ? 'line' : 'lines'} to the order
        </span>
      );
    } else if (blockingChecks > 0) {
      status = (
        <span className="nsb__status nsb__status--warn">
          ✕ {blockingChecks === 1 ? 'A difference' : `${blockingChecks} differences`} can't be confirmed — go
          back and correct the bill
        </span>
      );
    } else if (pendingChecks > 0) {
      status = (
        <span className="nsb__status nsb__status--pending">
          Confirm {pendingChecks === 1 ? 'the difference' : `${pendingChecks} differences`} to continue
        </span>
      );
    } else {
      status = (
        <span className="nsb__status nsb__status--ok">
          ✓ {mappingChecks.length > 0 ? 'Every line matched' : 'All confirmed'} — ready to create the bill
        </span>
      );
    }
  } else if (error?.uncertain) {
    status = <span className="nsb__status nsb__status--pending">⏳ Check NetSuite before creating it again</span>;
  } else if (jsonInvalid) {
    status = <span className="nsb__status nsb__status--warn">⚠ Fix the JSON to continue</span>;
  } else if (showErrors && errorCount > 0) {
    status = (
      <button type="button" className="nsb__status nsb__status--warn" onClick={focusFirstInvalid}>
        ⚠ {errorCount} {errorCount === 1 ? 'field needs' : 'fields need'} attention
      </button>
    );
  } else if (openIssuePaths.length > 0) {
    status = (
      <button type="button" className="nsb__status nsb__status--warn" onClick={() => showField(openIssuePaths[0])}>
        🔎 {openIssuePaths.length === 1 ? 'One name' : `${openIssuePaths.length} names`} to sort out
      </button>
    );
  } else if (errorCount > 0) {
    status = (
      <span className="nsb__status">
        {errorCount} required {errorCount === 1 ? 'value' : 'values'} still to fill
      </span>
    );
  } else {
    status = (
      <span className="nsb__status nsb__status--ok">
        ✓ Ready · {draft.items.length} {draft.items.length === 1 ? 'line' : 'lines'} ·{' '}
        {formatMoney(total, extracted.currency)}
      </span>
    );
  }

  const formBadge = review
    ? pendingChecks + blockingChecks + unmatched
    : (showErrors ? errorCount : 0) + openIssuePaths.length;

  const spinner = (
    <>
      <span className="nsb__spinner" aria-hidden="true" /> Creating bill…
    </>
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={dialogRef}
      className="nsb"
      aria-labelledby="nsb-title"
      onCancel={(e) => {
        e.preventDefault(); // Escape: route through requestClose so React stays the source of truth
        requestClose();
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) requestClose(); // backdrop click
      }}
    >
      <header className="nsb__head">
        <span className="nsb__mark" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M6 2h12v20l-3-2-3 2-3-2-3 2z" />
            <path d="M9 7h6M9 11h6M9 15h4" />
          </svg>
        </span>
        <div className="nsb__titles">
          <h2 className="nsb__title" id="nsb-title">
            {submission ? 'Bill created' : 'Create bill in NetSuite'}
          </h2>
          <p className="nsb__subtitle" title={filename}>
            {submission
              ? filename
              : review
                ? `${mappingChecks.length > 0 ? 'Match the purchase order lines' : 'Confirm the differences NetSuite found'} · ${filename}`
                : `Review, adjust and create · ${filename}`}
          </p>
        </div>
        <button
          type="button"
          className="nsb__close"
          onClick={requestClose}
          disabled={submitting}
          aria-label="Close"
        >
          ✕
        </button>
        {submitting && (
          <div className="nsb__progress" role="progressbar" aria-label="Creating bill in NetSuite" />
        )}
      </header>

      {submission ? (
        <SuccessView submission={submission} currency={extracted.currency} />
      ) : (
        <>
          <div className="nsb__tabs" role="tablist" aria-label="Editor view">
            <button
              type="button"
              role="tab"
              id="nsb-tab-form"
              aria-controls="nsb-pane-form"
              aria-selected={tab === 'form'}
              className={`nsb__tab${tab === 'form' ? ' is-active' : ''}`}
              onClick={() => setTab('form')}
            >
              {review ? 'Review' : 'Form'}
              {formBadge > 0 && <span className="nsb__tab-badge">{formBadge}</span>}
            </button>
            <button
              type="button"
              role="tab"
              id="nsb-tab-json"
              aria-controls="nsb-pane-json"
              aria-selected={tab === 'json'}
              className={`nsb__tab${tab === 'json' ? ' is-active' : ''}`}
              onClick={() => setTab('json')}
            >
              JSON
              {jsonInvalid && <span className="nsb__tab-badge">!</span>}
            </button>
          </div>

          <div className={`nsb__body nsb__body--show-${tab}`}>
            <div className="nsb__pane nsb__pane--form" id="nsb-pane-form" ref={formPaneRef}>
              {review ? (
                <NetSuiteReview
                  review={review.data}
                  confirmed={review.confirmed}
                  onToggle={toggleCheck}
                  mappingChecks={mappingChecks}
                  purchaseOrderLines={poLines}
                  mapping={review.mapping}
                  onMap={mapLine}
                  onMapAll={mapAll}
                  items={draft.items}
                  currency={extracted.currency}
                  disabled={submitting}
                  raw={review.raw}
                />
              ) : (
                <>
                  {error?.uncertain ? (
                    <TimeoutNotice
                      error={error}
                      payload={lastRequest || draft}
                      onFound={markFound}
                      onRetry={() => submit(lastRequest?.confirmedChecks)}
                    />
                  ) : (
                    error && <SubmitErrorBanner error={error} />
                  )}
                  {names && names.issues.length > 0 && (
                    <UnresolvedBanner
                      issues={names.issues}
                      open={issueErrors}
                      requestId={names.data.requestId}
                      raw={names.raw}
                      onShow={showField}
                    />
                  )}
                  <NetSuiteBillForm
                    payload={draft}
                    onChange={onDraftChange}
                    errors={formErrors}
                    disabled={submitting}
                    extracted={extracted}
                    unresolved={names?.data ?? null}
                  />
                </>
              )}
            </div>
            <div className="nsb__pane nsb__pane--json" id="nsb-pane-json">
              <JsonPayloadPane
                payload={preview}
                onChange={onDraftChange}
                onInvalidChange={setJsonInvalid}
                disabled={submitting || !!review}
              />
            </div>
          </div>
        </>
      )}

      <footer className="nsb__foot">
        {submission ? (
          <>
            {submission.payload.vendorEntityId && (
              <span className="nsb__status nsb__status--ok">
                ✓ {submission.payload.vendorEntityId} will be suggested for this vendor next time
              </span>
            )}
            <div className="nsb__foot-actions">
              <button type="button" className="nsb__btn nsb__btn--primary" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        ) : review ? (
          <>
            <div className="nsb__status-wrap" aria-live="polite">
              {status}
            </div>
            <div className="nsb__foot-actions">
              <button
                type="button"
                className="nsb__btn nsb__btn--secondary"
                onClick={() => setReview(null)}
                disabled={submitting}
              >
                ← Back to edit
              </button>
              <button
                type="button"
                className="nsb__btn nsb__btn--primary"
                onClick={confirmAndCreate}
                disabled={submitting || !reviewReady}
              >
                {submitting ? spinner : mappingChecks.length > 0 ? 'Match & create bill' : 'Confirm & create bill'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="nsb__status-wrap" aria-live="polite">
              {status}
            </div>
            <div className="nsb__foot-actions">
              <button
                type="button"
                className={`nsb__btn nsb__btn--quiet${confirmReset ? ' is-confirm' : ''}`}
                onClick={reset}
                disabled={submitting}
                title="Re-map every value from the extracted document"
              >
                {confirmReset ? 'Discard all edits?' : '↺ Reset'}
              </button>
              <button
                type="button"
                className="nsb__btn nsb__btn--secondary"
                onClick={requestClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="nsb__btn nsb__btn--primary"
                onClick={() => submit()}
                // After a timeout, retrying must be a deliberate choice in the notice.
                disabled={submitting || jsonInvalid || !!error?.uncertain}
              >
                {submitting ? spinner : 'Create bill'}
              </button>
            </div>
          </>
        )}
      </footer>
    </dialog>
  );
};

export default NetSuiteBillDialog;
